import { FaSortUp, FaSortDown, FaSort } from '@meronex/icons/fa';
import React, { KeyboardEvent } from 'react';
import { shallowEqual } from 'react-redux';
import { Box, ThemeUIStyleObject } from '@apia/theme';
import { domPropsSelector, rtDebug } from '.';
import { useAppSelector } from '../../../../hooks/hooks';
import useRenderDebug from '../../../../hooks/useRenderDebug';
import store from '../../../../store';
import useDomProps from '../../../../store/selection/useDomProps';
import { TMap } from '../../../../types';
import debugDispatcher from '../../../../utils/debugDispatcher';
import { getSpecificParent, isChild } from '../../../../utils/dom';
import persistentStorage from '../../../../utils/persistentStorage';
import EventEmitter from '../../../../utils/util/eventEmitter';
import useOtherTagButton from '../../buttons/useOtherTagButton';
import { useResponsiveTableContext } from './ResponsiveTableContext';
import { TResponsiveTableSortValue } from './types';

interface IHeaderCell {
  /**
   * Para ordenar en Apia se usa un índice de columna, que se
   * corresponde con su posición en base 0, sin tomar en cuenta
   * las columnas adicionales.
   */
  apiaColumnIndex: number;
  columnIndex: number;
  inTableColumnIndex: number;
}

const widthStorageName = 'tablesWidths';

/**
 * Calcula el ancho de la columna dependiendo del valor establecido,
 * si el valor es numérico o un string que contiene un número sin
 * unidad, devuelve el doble de ese número para establecer una
 * relatividad con los tamaños de las columnas actuales en Apia.
 *
 * Es decir, como todo en Apia era más chico, los tamaños que se
 * establecían antes no quedan visualmente agradables con los
 * tamaños actuales, por eso se magnifican multiplicando por 2.
 */
function getColumnWidth(width?: string | number) {
  if (typeof width === 'string') {
    if (width.match(/(%|px|vw|vh|vmin|vmax|cm|mm|in|pt|pc)/)) return width;
    return Number.parseInt(width, 10);
  }
  if (typeof width === 'number') return width;
  return width;
}
debugDispatcher.on(
  'tablesWidths',
  () => {
    console.log({
      ...(persistentStorage[widthStorageName] as Record<string, unknown>),
    });
  },
  'Se usa para ver las tablas que estan modificadas en el storage y sus medidas',
);
debugDispatcher.on(
  'cleanTableSizes',
  ([tableName]) => {
    console.log('removeTable ', tableName);
    const currentWidths = persistentStorage[widthStorageName] as TMap;
    if (tableName === '') {
      // persistentStorage.remove(widthStorageName);
      window.localStorage.removeItem('tablesWidths');
    }
    delete currentWidths[tableName as string];
    persistentStorage[widthStorageName] = currentWidths;
  },
  'Se usa para borrar el estado de una tabla. Uso: dd.cleanTableSizes("TableName")',
);
function makeColumnName(tableName: string, columnName: string): string {
  return `${tableName}${columnName}`;
}

const thWidthEmitter = new (class ThWidthEmitter extends EventEmitter<{
  resize: { name: string; width: number };
}> {})();

const HeaderCell = ({
  apiaColumnIndex,
  columnIndex,
  inTableColumnIndex,
}: IHeaderCell) => {
  const { name, onSortChange } = useResponsiveTableContext();
  const uniqueId =
    (window.location.href.match(/busEntId=(\d+)/)?.[1] as string) ??
    (window.location.href.match(/query=(\d+)/)?.[1] as string) ??
    '';
  const isGlobal = window.isGlobal ? 'isGlobal' : 'notGlobal';
  const actualName = name + uniqueId + String(isGlobal);
  const column = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.columns[columnIndex];
  }, shallowEqual);
  const storedWidth = (
    (persistentStorage[widthStorageName] as TMap)?.[actualName] as TMap
  )?.[column.name] as number;

  const [width, setStateWidth] = React.useState(
    storedWidth ?? column.width ?? 200,
  );
  const setWidth = (newWidth: number) => {
    isResized.current = true;
    setStateWidth(Math.max(50, newWidth));
  };
  const isResized = React.useRef(storedWidth !== undefined);

  React.useEffect(() => {
    return void thWidthEmitter.on('resize', (ev) => {
      if (ev.name === makeColumnName(actualName, column.name)) {
        isResized.current = true;
        setWidth(ev.width);
      }
    });
  }, [column.name, actualName]);

  const isFilterRequired = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];
    const columnFilter = tableState?.filters.find(
      (current) => current.column === column.name,
    );
    return columnFilter?.required;
  }, shallowEqual);

  const isSortingAllowed = useAppSelector((global) => {
    const tableState = global.responsiveTableSlice[name];
    return tableState?.allowSorting;
  });

  const domProps = useDomProps(
    'th',
    {
      columnIndex: inTableColumnIndex,
    },
    domPropsSelector(name),
  );

  useRenderDebug(rtDebug.headerCell, 'HeaderCell.tsx', {
    columnIndex,
    column,
    domProps,
  });

  const sx: ThemeUIStyleObject = React.useMemo(
    () =>
      !isResized
        ? {
            minWidth: getColumnWidth(column.minWidth ?? column.width),
            maxWidth: getColumnWidth(column.maxWidth ?? column.width),
            width: getColumnWidth(column.width),
            position: 'relative',
          }
        : {
            width: `${width.toString().endsWith('px') ? width : width + 'px'}`,
            minWidth: `${width.toString().endsWith('px') ? width : width + 'px'}`,
            maxWidth: `${width.toString().endsWith('px') ? width : width + 'px'}`,
            position: 'relative',
          },
    [column.maxWidth, column.minWidth, column.width, isResized, width],
  );

  const buttonProps = useOtherTagButton(
    React.useMemo(
      () => ({
        className: 'headButton',
        onKeyDown: (ev: KeyboardEvent) => {
          if (ev.code === 'Enter') {
            ev.stopPropagation();
            if (
              store
                .getState()
                .buttonsSlice.performingActions.includes('disableTable')
            )
              return;
            if (
              onSortChange &&
              column.allowSorting !== false &&
              isSortingAllowed !== false
            )
              onSortChange({
                column,
                columnIndex: apiaColumnIndex,
                name: column.name,
                sortValue: ['Asc', 'Desc'][
                  column.currentSorting !== 'A' ? 0 : 1
                ] as TResponsiveTableSortValue,
              });
          }
        },
        onClick: (ev) => {
          if (
            isChild(ev.target as HTMLElement, (current) =>
              current.classList.contains('headButton__resizer'),
            )
          )
            return;
          if (
            store
              .getState()
              .buttonsSlice.performingActions.includes('disableTable')
          )
            return;
          if (
            onSortChange &&
            column.allowSorting !== false &&
            isSortingAllowed !== false
          )
            onSortChange({
              column,
              columnIndex: apiaColumnIndex,
              name: column.name,
              sortValue: ['Asc', 'Desc'][
                column.currentSorting !== 'A' ? 0 : 1
              ] as TResponsiveTableSortValue,
            });
        },
      }),
      [apiaColumnIndex, column, isSortingAllowed, onSortChange],
    ),
  );
  return (
    <th
      sx={sx}
      data-columnname={column.name}
      title={column.title || column.label || column.name}
      {...buttonProps}
      {...domProps}
      role="rowheader"
      className="responsiveTable__headerButton"
      ref={(el) => {
        if (!column?.autoAdjustWidth) return;
        if (
          el instanceof HTMLElement &&
          !el.dataset.resized &&
          storedWidth === undefined
        ) {
          const clone = el.cloneNode(true) as HTMLElement;
          const child = clone.querySelector(
            '.headButton__label',
          ) as HTMLElement;
          child.style.opacity = '0';
          document.body.append(child);
          child.style.position = 'fixed';
          child.style.maxWidth = '0';
          child.style.minWidth = '0';
          child.style.width = '0';
          child.style.overflow = 'auto';
          child.style.textWrap = 'nowrap';

          window.requestAnimationFrame(() => {
            const idealWidth = child.scrollWidth;
            const currentWidth = el
              .querySelector('.headButton__label')!
              .getBoundingClientRect().width;
            const elWidth = el.getBoundingClientRect().width;
            const diff = idealWidth - currentWidth;
            setWidth(elWidth + diff + 5);

            el.dataset.resized = 'true';
          });
        }
      }}
    >
      <Box as="span" className="headButton__container">
        <Box as="span" className="headButton__label">
          <Box
            as="span"
            className={`${isFilterRequired ? 'requiredFilter__Column' : ''}`}
          >
            {column.label}
          </Box>
        </Box>
        <Box as="span" className="headButton__sortIcon">
          {column.allowSorting !== false && column.currentSorting === 'A' && (
            <FaSortUp />
          )}
          {column.allowSorting !== false && column.currentSorting === 'D' && (
            <FaSortDown />
          )}
          {column.allowSorting !== false && !column.currentSorting && (
            <FaSort />
          )}
        </Box>
        <Box
          // sx={{ display: 'none' }}
          className="headButton__resizer"
          onMouseDown={(ev) => {
            if (!isResized.current) {
              const trElement = getSpecificParent(
                ev.target as HTMLElement,
                (current) => current.tagName === 'TR',
              );

              trElement?.querySelectorAll('th').forEach((current) => {
                thWidthEmitter.emit('resize', {
                  name: makeColumnName(
                    actualName,
                    current.dataset.columnname as string,
                  ),
                  width: (current as HTMLElement).clientWidth,
                });
              });

              isResized.current = true;
            }

            const th = getSpecificParent(
              ev.target as HTMLElement,
              (current) => current.tagName === 'TH',
            );
            if (!th) throw new Error('There is no th');

            const initialX = ev.clientX;
            const initialWidth = th.clientWidth;

            const resize = (mousemoveEv: MouseEvent) => {
              const dif = mousemoveEv.clientX - initialX;

              setWidth(initialWidth + dif);
            };
            const unsuscribe = () => {
              const trElement = getSpecificParent(
                ev.target as HTMLElement,
                (current) => current.tagName === 'TR',
              );
              trElement?.querySelectorAll('th').forEach((current) => {
                persistentStorage[widthStorageName] = {
                  ...(persistentStorage[widthStorageName] as TMap),
                  [actualName]: {
                    ...((persistentStorage[widthStorageName] as TMap)?.[
                      actualName
                    ] as TMap),
                    [current.dataset.columnname as string]: (
                      current as HTMLElement
                    ).clientWidth,
                  },
                };
              });
              document.removeEventListener('mousemove', resize);
              document.removeEventListener('mouseup', unsuscribe);
            };

            document.addEventListener('mousemove', resize);
            document.addEventListener('mouseup', unsuscribe);

            return false;
          }}
        />
      </Box>
    </th>
  );
};

export default React.memo(HeaderCell);
